import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  AreaChart, Area, Legend,
} from "recharts";
import { ArrowRight, Gauge } from "lucide-react";

const LEVEL_COLOR = {
  Critical: "hsl(var(--destructive))",
  High:     "hsl(var(--warning))",
  Elevated: "hsl(var(--primary))",
  Nominal:  "hsl(var(--success))",
};

const STATUS_COLOR = {
  Clear:    "hsl(var(--success))",
  Light:    "hsl(var(--primary))",
  Heavy:    "hsl(var(--warning))",
  Critical: "hsl(var(--destructive))",
};

const barColor = (pct) => {
  if (pct >= 0.9)  return "hsl(var(--destructive))";
  if (pct >= 0.75) return "hsl(var(--warning))";
  if (pct >= 0.55) return "hsl(var(--primary))";
  return "hsl(217 91% 60%)";
};

const fmtInt = (n) => Number(n ?? 0).toLocaleString("en-IN");
const fmtSec = (s) => {
  const v = Number(s ?? 0);
  if (v < 60) return `${Math.round(v)}s`;
  const m = Math.floor(v / 60);
  return `${m}m ${Math.round(v - m * 60)}s`;
};

const StandAttendanceChart = ({ data = [] }) => {
  const series = data.map((d) => ({ name: d.name.replace(" Stand", ""), pct: Math.round(d.pct * 100), occupancy: d.occupancy, capacity: d.capacity }));
  return (
    <div className="h-44" data-testid="crowd-stand-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 5, right: 8, left: -24, bottom: 0 }}>
          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} interval={0} angle={-25} dy={6} height={36} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} unit="%" width={32} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }}
            formatter={(v, _n, p) => [`${v}% · ${fmtInt(p?.payload?.occupancy)}/${fmtInt(p?.payload?.capacity)}`, "Occupancy"]}
          />
          <Bar dataKey="pct" radius={[1, 1, 0, 0]} isAnimationActive={false}>
            {series.map((d) => (
              <Cell key={d.name} fill={barColor(d.pct / 100)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const CongestionGauge = ({ panic = 0, level = "Nominal", components }) => {
  // Half-arc gauge using a simple SVG.
  const value = Math.max(0, Math.min(1, panic));
  const angle = -90 + value * 180; // -90 → +90 degrees
  const color = LEVEL_COLOR[level] || "hsl(var(--primary))";
  const r = 60, cx = 80, cy = 78;
  const trackPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const valueLen = (value * Math.PI * r);
  const totalLen = Math.PI * r;

  return (
    <div className="flex flex-col items-center gap-1" data-testid="crowd-congestion-gauge">
      <svg viewBox="0 0 160 100" className="w-full max-w-[220px]">
        <path d={trackPath} fill="none" stroke="hsl(var(--muted))" strokeWidth="10" strokeLinecap="round" />
        <path
          d={trackPath}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${valueLen} ${totalLen - valueLen}`}
        />
        {/* needle */}
        <g transform={`rotate(${angle} ${cx} ${cy})`}>
          <line x1={cx} y1={cy} x2={cx} y2={cy - r + 6} stroke="hsl(var(--foreground))" strokeWidth="2" strokeLinecap="round" />
        </g>
        <circle cx={cx} cy={cy} r="3" fill="hsl(var(--foreground))" />
        <text x={cx} y={cy - 18} textAnchor="middle" fontFamily="Chivo, sans-serif" fontWeight="900" fontSize="22" fill={color}>
          {Math.round(value * 100)}
        </text>
        <text x={cx} y={cy - 4} textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="9" fill="hsl(var(--muted-foreground))" letterSpacing="2">
          PANIC INDEX
        </text>
      </svg>
      <div className="font-display font-bold tracking-tight text-xs" style={{ color }} data-testid="crowd-congestion-level">{level}</div>
      {components && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-mono text-muted-foreground mt-1">
          <div>Avg Density</div><div className="text-right text-foreground">{Math.round(components.avg_density * 100)}%</div>
          <div>Peak Zone</div><div className="text-right text-foreground">{Math.round(components.peak_pct * 100)}%</div>
          <div>Critical 5m</div><div className="text-right text-foreground">{components.critical_events_5m}</div>
        </div>
      )}
    </div>
  );
};

const FlowTrendChart = ({ data = [] }) => {
  const series = data.map((d) => ({
    t: new Date(d.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    entries: d.entries,
    exits: d.exits,
  }));
  return (
    <div className="h-40" data-testid="crowd-flow-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
          <defs>
            <linearGradient id="flow-entry" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="flow-exit" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--warning))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="hsl(var(--warning))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} interval="preserveStartEnd" />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} width={32} />
          <Tooltip contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }} />
          <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
          <Area type="monotone" name="Entries" dataKey="entries" stroke="hsl(var(--primary))" fill="url(#flow-entry)" strokeWidth={1.5} isAnimationActive={false} />
          <Area type="monotone" name="Exits"   dataKey="exits"   stroke="hsl(var(--warning))" fill="url(#flow-exit)"  strokeWidth={1.5} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const GateQueueTable = ({ rows = [] }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[11px]" data-testid="crowd-gate-queue-table">
      <thead>
        <tr className="text-left font-mono text-[10px] text-muted-foreground tracking-wide border-b border-border">
          <th className="py-1.5 px-2 font-medium">Gate</th>
          <th className="py-1.5 px-2 font-medium text-right">Rate /min</th>
          <th className="py-1.5 px-2 font-medium text-right">Queue</th>
          <th className="py-1.5 px-2 font-medium text-right">Wait</th>
          <th className="py-1.5 px-2 font-medium">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.gate_id}
            className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
            data-testid={`crowd-gate-row-${r.gate_id}`}
          >
            <td className="py-1.5 px-2 font-medium">{r.name}</td>
            <td className="py-1.5 px-2 text-right font-mono">{r.rate}</td>
            <td className="py-1.5 px-2 text-right font-mono">{r.queue}</td>
            <td className="py-1.5 px-2 text-right font-mono">{fmtSec(r.wait_sec)}</td>
            <td className="py-1.5 px-2">
              <span
                className="px-1.5 py-0.5 text-[9px] font-bold tracking-wide border"
                style={{ color: STATUS_COLOR[r.status] || "hsl(var(--muted-foreground))", borderColor: STATUS_COLOR[r.status] || "hsl(var(--border))" }}
              >
                {r.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CrowdIntelligencePanel = ({ data }) => {
  const navigate = useNavigate();
  const d = data || { stand_attendance: [], flow_trend: [], gate_queues: [], congestion: { panic_index: 0, level: "Nominal" } };
  return (
    <section className="space-y-3" data-testid="dashboard-section-crowd">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold tracking-tight text-xs">Crowd Intelligence</h2>
        </div>
        <button
          onClick={() => navigate("/crowd")}
          className="btn-tac !px-2 !py-1 text-[10px]"
          data-testid="crowd-open-page-btn"
        >
          Open Crowd Intel <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="panel p-3 lg:col-span-2">
          <div className="h-widget mb-2">Stand-Wise Attendance</div>
          <StandAttendanceChart data={d.stand_attendance} />
        </div>
        <div className="panel p-3">
          <div className="h-widget mb-2 flex items-center gap-1">
            <Gauge className="w-3 h-3" /> Congestion Risk
          </div>
          <CongestionGauge
            panic={d.congestion?.panic_index}
            level={d.congestion?.level}
            components={d.congestion}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="panel p-3">
          <div className="h-widget mb-2">Entry / Exit Trend</div>
          <FlowTrendChart data={d.flow_trend} />
        </div>
        <div className="panel p-3">
          <div className="h-widget mb-2">Gate Queue Analytics</div>
          <GateQueueTable rows={d.gate_queues} />
        </div>
      </div>
    </section>
  );
};

export default CrowdIntelligencePanel;
